/**
 * Herstelcode voor leerlingaccounts
 * ═════════════════════════════════
 *
 * WAAROM DIT BESTAAT
 *
 * Een leerling heeft geen e-mailadres. Zijn auth-adres eindigt op `.invalid`
 * en kan per definitie geen post ontvangen, dus de normale route "stuur een
 * herstelmail" bestaat voor hem niet. Vergeet hij zijn wachtwoord, dan is hij
 * zonder vangnet zijn account én zijn voortgang kwijt.
 *
 * De database had dat vangnet al: `set_recovery_code()` slaat een bcrypt-hash
 * op, `verify_recovery_code()` controleert hem, en de Edge Function
 * `recover-account` zet daarna een nieuw wachtwoord. Alleen werd
 * `set_recovery_code()` nergens aangeroepen — er was dus nooit een hash, en
 * élke herstelpoging faalde met "gebruikersnaam en herstelcode horen niet bij
 * elkaar". Niet te onderscheiden van een typefout, dus het viel ook niemand
 * op. Dit bestand is de ontbrekende helft.
 *
 *
 * WAAROM WOORDEN EN GEEN TEKENREEKS
 *
 * `k7Qv-2mXp` is voor een negenjarige onoverschrijfbaar: de 0/O, 1/l en 5/S
 * zijn niet uit elkaar te houden, zeker niet als een ouder het door de
 * telefoon voorleest. Zes woorden zijn langer om op te schrijven maar
 * betrouwbaar over te nemen, en een kind kan ze desnoods voorlezen aan een
 * volwassene zonder dat er iets misgaat.
 *
 * Zes woorden uit een lijst van 64 geeft 64^6 ≈ 7·10^10 combinaties. Dat is
 * ruim voldoende, omdat de Edge Function bovendien afknijpt op pogingen — de
 * code hoeft niet bestand te zijn tegen offline kraken, want hij staat alleen
 * als bcrypt-hash op de server.
 *
 *
 * WAT WE BEWUST NIET DOEN
 *
 * De code wordt nergens lokaal bewaard. Niet in localStorage, niet in de
 * accountstore, niet in een log. Hij bestaat één keer op het scherm en
 * daarna alleen als hash op de server — ook wij kunnen hem niet teruglezen.
 * Dat is geen extra strengheid maar noodzaak: de herstelcode is voor een kind
 * zonder e-mailadres tegelijk het identiteitsbewijs waarmee het een
 * AVG-inzage- of verwijderverzoek kan doen. Zouden wij hem kunnen lezen, dan
 * kunnen wij ons als dat kind voordoen.
 *
 * Een mislukte opslag is daarom géén reden om de registratie te laten
 * mislukken: het account bestaat dan gewoon, alleen zonder vangnet. Het kind
 * een werkend account afnemen omdat een tweede verzoek faalde, is een
 * grotere schade dan het ontbrekende vangnet. De aanroeper hoort de code in
 * dat geval niet te tonen — een opgeschreven code die niets opent, is erger
 * dan geen code, want het kind denkt beschermd te zijn.
 */

import { supabase } from './client';
import { error as logError } from '../../utils/logger';

/**
 * 64 woorden, elk 4-9 letters.
 *
 * Gekozen op overschrijfbaarheid, niet op variatie: geen woorden die in klank
 * of spelling op elkaar lijken, geen dubbele klinkers die verkeerd geteld
 * worden, geen woorden met 'ij' naast 'ei'. Alleen concrete zelfstandige
 * naamwoorden waar een kind een plaatje bij heeft, want een woord dat je kunt
 * zien onthoud je bij het overschrijven.
 *
 * Bewust geen woorden die iets over een persoon kunnen zeggen — de code komt
 * op papier terecht dat een ander kan vinden.
 */
const RECOVERY_WORDS = ['anker', 'appel', 'ballon', 'bever', 'bloem', 'boot', 'brug', 'cactus', 'dolfijn', 'draak', 'druif', 'egel', 'fluit', 'gitaar', 'haven', 'hamer', 'helm', 'hout', 'kaars', 'kameel', 'kano', 'kasteel', 'kikker', 'koala', 'komeet', 'kompas', 'kroon', 'lamp', 'lantaarn', 'maan', 'molen', 'mossel', 'noot', 'olifant', 'otter', 'panda', 'parel', 'planeet', 'raket', 'regen', 'ridder', 'rivier', 'robot', 'rots', 'schelp', 'schip', 'sleutel', 'sneeuw', 'ster', 'stoel', 'strand', 'tent', 'tijger', 'toren', 'trommel', 'tulp', 'uil', 'vlinder', 'vos', 'vulkaan', 'wolk', 'zebra', 'zeil', 'zwaan'] as const;

/** Aantal woorden. Zes geeft ruim genoeg ruimte en past nog op één regel. */
const WORD_COUNT = 6;

/**
 * Bouw een nieuwe herstelcode.
 *
 * `crypto.getRandomValues` en niet `Math.random`: dit is een geheim. Een
 * voorspelbare reeks zou betekenen dat wie het tijdstip van registratie kent,
 * de code kan reconstrueren.
 *
 * Woorden mogen herhalen. Ze uitsluiten zou de ruimte juist verkleinen en
 * levert bovendien een subtiel signaal op ("er zit geen woord dubbel in"),
 * terwijl het voor het kind niets makkelijker maakt.
 */
export function generateRecoveryCode(): string {
  const rand = new Uint32Array(WORD_COUNT);
  crypto.getRandomValues(rand);
  const words: string[] = [];
  for (let i = 0; i < WORD_COUNT; i++) {
    words.push(RECOVERY_WORDS[rand[i] % RECOVERY_WORDS.length]);
  }
  return words.join('-');
}

/**
 * Sla de herstelcode op voor de INGELOGDE gebruiker.
 *
 * De RPC werkt uitsluitend op `auth.uid()`, dus er kan geen id meegegeven
 * worden en niemand kan de herstelcode van een ander zetten. Dat betekent wel
 * dat dit pas kan nadat de sessie bestaat — bij registratie dus ná `signUp`,
 * niet ervoor.
 *
 * Retourneert `false` in plaats van te gooien: de aanroeper moet zelf kunnen
 * beslissen dat een ontbrekend vangnet geen reden is om de registratie af te
 * breken.
 */
export async function storeRecoveryCode(code: string): Promise<boolean> {
  try {
    const {
      error
    } = await supabase.rpc('set_recovery_code', {
      p_code: code
    });
    if (error) {
      // De code zelf komt NIET in de log. Een herstelcode in een logregel is
      // een herstelcode die iemand anders kan lezen.
      logError('[recoveryCode] Opslaan mislukt', error);
      return false;
    }
    return true;
  } catch (e) {
    logError('[recoveryCode] Opslaan mislukt', e);
    return false;
  }
}

/**
 * Genereer en sla in één keer op.
 *
 * Geeft de code terug als het opslaan lukte, anders `null`. Die koppeling is
 * bewust: er is geen situatie waarin je een code wilt tonen die de server niet
 * kent, en door alleen bij succes iets terug te geven kan de aanroeper die
 * fout niet maken.
 */
export async function createAndStoreRecoveryCode(): Promise<string | null> {
  const code = generateRecoveryCode();
  const ok = await storeRecoveryCode(code);
  return ok ? code : null;
}

/**
 * Heeft de ingelogde gebruiker een herstelcode staan, en sinds wanneer?
 *
 * Alleen de DATUM, nooit de code — die is bcrypt-gehasht en ook door ons niet
 * terug te lezen. Dat is precies de bedoeling, en het is de reden dat de
 * instellingen geen "toon mijn code" kunnen bieden maar alleen "maak een
 * nieuwe".
 *
 * `null` betekent "niet vast te stellen" (netwerkfout), niet "geen code". Dat
 * onderscheid is nodig: bij een storing een waarschuwing tonen dat er geen
 * vangnet is, zou een kind laten denken dat het onbeschermd is terwijl het dat
 * niet is.
 */
export async function fetchRecoveryCodeStatus(): Promise<{
  hasCode: boolean;
  setAt: string | null;
} | null> {
  try {
    const {
      data: auth
    } = await supabase.auth.getUser();
    const uid = auth.user?.id;
    if (!uid) return null;
    const {
      data,
      error
    } = await supabase.from('profiles').select('recovery_code_set_at').eq('id', uid).maybeSingle();
    if (error) {
      logError('[recoveryCode] Status ophalen mislukt', error);
      return null;
    }
    const setAt = data?.recovery_code_set_at as string | null ?? null;
    return {
      hasCode: setAt !== null,
      setAt
    };
  } catch (e) {
    logError('[recoveryCode] Status ophalen mislukt', e);
    return null;
  }
}